if (!customElements.get("scrolling-text-banner")) {
  customElements.define(
    "scrolling-text-banner",

    class ScrollingTextBanner extends HTMLElement {
      constructor() {
        super();
        this.spacing = 20; // Default spacing between phrases
        this.speedFactor = 10; // Base speed multiplier
        this.speed = 100; // Initial speed (calculated later)
        this.mobileBreakpoint = 600; // Width threshold for mobile devices
        this.mobileSpeedFactor = 0.7; // Default factor for mobile speed adjustment
        this.resizeObserver = null; // For observing size changes
      }

      connectedCallback() {
        // Read spacing and speed from data attributes
        this.spacing =
          parseInt(this.getAttribute("data-spacing")) || this.spacing;

        // Read speed in range 1-10 and map to actual speed
        const speedInput = parseInt(this.getAttribute("data-speed")) || 5; // Default to 5
        this.speed = this.mapSpeed(speedInput);

        // Read mobile speed factor from data attribute
        this.mobileSpeedFactor =
          parseFloat(this.getAttribute("data-mobile-speed-factor")) ||
          this.mobileSpeedFactor;

        // Initialize the scrolling text
        this.initializeScrollingText();

        // Add resize listener
        this.observeResizing();
      }

      disconnectedCallback() {
        // Clean up resize observer if the component is removed
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      mapSpeed(input) {
        // Map input range [1, 10] to speed in pixels per second
        const minSpeed = 30; // Slowest speed in pixels per second
        const maxSpeed = 300; // Fastest speed in pixels per second
        return minSpeed + (maxSpeed - minSpeed) * ((input - 1) / 9);
      }

      getAdjustedSpeed() {
        // Adjust speed based on the mobile breakpoint
        if (window.innerWidth < this.mobileBreakpoint) {
          return this.speed * this.mobileSpeedFactor;
        }
        return this.speed;
      }

      initializeScrollingText() {
        // Find all `.hero__title` elements inside the component (for multiple heading blocks)
        const heroTitles = this.querySelectorAll(".hero__title");
        if (!heroTitles || heroTitles.length === 0) {
          console.error(
            "No `.hero__title` elements found inside the scrolling-text-banner component.",
          );
          return;
        }

        // Collect all text content from heading blocks
        const textContents = [];
        heroTitles.forEach((title) => {
          const text = title.textContent.trim();
          if (text) {
            textContents.push(text);
          }
        });

        if (textContents.length === 0) {
          console.error(
            "No text content found inside the `.hero__title` elements.",
          );
          return;
        }

        // Use the first hero__title as the container (or create a new one if needed)
        const firstHeroTitle = heroTitles[0];
        
        // Clear all original hero__title elements
        heroTitles.forEach((title) => {
          title.innerHTML = "";
        });

        // Create a container for the scrolling wrappers in the first title
        const scrollingContainer = document.createElement("div");
        scrollingContainer.classList.add("scrolling-container");
        firstHeroTitle.appendChild(scrollingContainer);

        // Hide other hero__title elements since we're using the first one as container
        for (let i = 1; i < heroTitles.length; i++) {
          heroTitles[i].style.display = "none";
        }

        // Create two wrappers for seamless animation
        for (let i = 0; i < 2; i++) {
          const wrapper = document.createElement("div");
          wrapper.classList.add("scrolling-wrapper");

          // Create and append text items for each heading, repeating the sequence
          for (let j = 0; j < 10; j++) {
            // Cycle through all headings
            textContents.forEach((textContent) => {
              const textWrapper = document.createElement("div");
              textWrapper.classList.add("scrolling-text");
              textWrapper.textContent = textContent;
              textWrapper.style.marginRight = `${this.spacing}px`; // Apply spacing
              wrapper.appendChild(textWrapper);
            });
          }

          // Append the wrapper to the container
          scrollingContainer.appendChild(wrapper);
        }

        // Set animation speed dynamically
        this.setAnimationSpeed(scrollingContainer);
      }

      setAnimationSpeed(scrollingContainer) {
        // Wait for layout to calculate sizes
        requestAnimationFrame(() => {
          const wrappers =
            scrollingContainer.querySelectorAll(".scrolling-wrapper");
          if (wrappers.length !== 2) return;

          const wrapperWidth = wrappers[0].offsetWidth; // Dynamic width of the first wrapper
          const adjustedSpeed = this.getAdjustedSpeed(); // Adjust speed for mobile
          const duration = wrapperWidth / adjustedSpeed; // Use pre-calculated speed

          // Apply calculated width and animation styles
          scrollingContainer.style.width = `${wrapperWidth * 2}px`; // Allow two wrappers to fit seamlessly
          wrappers.forEach((wrapper, index) => {
            wrapper.style.animationDuration = `${duration}s`;
            wrapper.style.animationDelay = `${-index * duration}s`; // Offset the second wrapper
          });
        });
      }

      observeResizing() {
        // Observe the size of the first `.hero__title` element (container)
        const heroTitle = this.querySelector(".hero__title");
        if (!heroTitle) return;

        this.resizeObserver = new ResizeObserver(() => {
          this.recalculateAnimation();
        });
        this.resizeObserver.observe(heroTitle);
      }

      recalculateAnimation() {
        const scrollingContainer = this.querySelector(".scrolling-container");
        if (scrollingContainer) {
          this.setAnimationSpeed(scrollingContainer);
        }
      }
    },
  );
}
